/**
 * Dates and times in the salon's zone. Every date string is YYYY-MM-DD in
 * Asia/Bishkek; the server sends every instant with its offset (doc 04 §1).
 * Kyrgyzstan has no daylight saving, so a Bishkek wall time is always +06:00;
 * the API still stores and returns full instants.
 */
import type { Locale } from '../types';

export const ZONE = 'Asia/Bishkek';
const OFFSET = '+06:00';

const ymdFormat = new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
const hmFormat = new Intl.DateTimeFormat('en-GB', { timeZone: ZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/** The Bishkek calendar date of an instant: '2026-10-01'. */
export function ymd(at: string | Date): string {
  return ymdFormat.format(typeof at === 'string' ? new Date(at) : at);
}

/** 'HH:MM' in Bishkek. */
export function hm(at: string | Date): string {
  return hmFormat.format(typeof at === 'string' ? new Date(at) : at);
}

/** Minutes since Bishkek midnight. */
export function minuteOfDay(at: string | Date): number {
  const [h, m] = hm(at).split(':').map(Number);
  return h * 60 + m;
}

/** 'HH:MM:SS' or 'HH:MM' -> minutes. */
export function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

export function minutesToHm(total: number): string {
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** A Bishkek wall time as an instant the API accepts. */
export function instant(day: string, hhmm: string): string {
  return `${day}T${hhmm}:00${OFFSET}`;
}

/** Calendar arithmetic on YYYY-MM-DD, independent of the browser's zone. */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** 0 = Monday ... 6 = Sunday, like working_hours.weekday. */
export function weekday(day: string): number {
  return (new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7;
}

export function mondayOf(day: string): string {
  return addDays(day, -weekday(day));
}

function cap(s: string): string {
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
}

/**
 * Kyrgyz names, fixed. Chromium ships no Kyrgyz date data and silently falls
 * back to English («Thursday, Oct 1», seen 2026-09-29), so Kyrgyz never goes
 * through Intl. The names are Node 24's own Kyrgyz output (CLDR), so every
 * browser shows what doc 07 §3 measured.
 */
export const KY_NAMES = {
  month: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'],
  monthShort: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
  weekday: ['дүйшөмбү', 'шейшемби', 'шаршемби', 'бейшемби', 'жума', 'ишемби', 'жекшемби'],
};

function parts(day: string, locale: Locale, month: 'long' | 'short') {
  if (locale === 'ky') {
    const m = Number(day.slice(5, 7)) - 1;
    const names = month === 'long' ? KY_NAMES.month : KY_NAMES.monthShort;
    return { weekdayName: KY_NAMES.weekday[weekday(day)], dm: `${Number(day.slice(8))}-${names[m]}` };
  }
  const date = new Date(`${day}T12:00:00${OFFSET}`);
  const weekdayName = new Intl.DateTimeFormat(locale, { timeZone: ZONE, weekday: 'long' }).format(date);
  const dm = new Intl.DateTimeFormat(locale, { timeZone: ZONE, day: 'numeric', month }).format(date);
  return { weekdayName, dm: dm.replace(/\.$/, '') };
}

/** «Четверг, 24 сентября» / «Бейшемби, 24-сентябрь» -- the mockup's app bar (D-53). */
export function dayTitle(day: string, locale: Locale): string {
  const { weekdayName, dm } = parts(day, locale, 'long');
  return `${cap(weekdayName)}, ${dm}`;
}

/** «Четверг, 24 сент» -- the shorter title on 06-08. */
export function dayTitleShort(day: string, locale: Locale): string {
  const { weekdayName, dm } = parts(day, locale, 'short');
  return `${cap(weekdayName)}, ${dm}`;
}

/** «10 сент» / «10-сен» -- the last-visit date on screen 11. */
export function dayMonthShort(day: string, locale: Locale): string {
  return parts(day, locale, 'short').dm;
}

/** «21–27 сентября» / «21–27-сентябрь». */
export function weekTitle(monday: string, locale: Locale): string {
  const sunday = addDays(monday, 6);
  const end = parts(sunday, locale, 'long').dm;
  const startDay = Number(monday.slice(8));
  if (monday.slice(5, 7) === sunday.slice(5, 7)) return `${startDay}–${end}`;
  return `${parts(monday, locale, 'long').dm} – ${end}`;
}

/** «Сентябрь 2026» / «2026-жыл сентябрь». */
export function monthTitle(yyyyMm: string, locale: Locale): string {
  const date = new Date(`${yyyyMm}-15T12:00:00${OFFSET}`);
  const year = yyyyMm.slice(0, 4);
  if (locale === 'ky') return `${year}-жыл ${KY_NAMES.month[Number(yyyyMm.slice(5, 7)) - 1]}`;
  const name = new Intl.DateTimeFormat(locale, { timeZone: ZONE, month: 'long' }).format(date);
  return `${cap(name)} ${year}`;
}

/** «Чт, 24.09.2026» -- the date box on 06 (D-73). Weekday names come from the
 *  string bundle (book.weekdayInDate), Monday first, like weekday(). */
export function dateWithWeekday(day: string, names: readonly string[]): string {
  return `${names[weekday(day)]}, ${day.split('-').reverse().join('.')}`;
}
